import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ProfileForm } from '@/components/ProfileForm';
import { fontSize, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

import { useProfile } from '@/contexts/ProfileContext';
import { claimPendingInviteAgreement, consumePendingInviteAgreement } from '@/lib/invite';

export default function OnboardingScreen() {
  const { t } = useTranslation();
  const { profile, save } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <Text style={styles.title}>{t('onboarding.title')}</Text>
          <Text style={styles.subtitle}>
            {t('onboarding.subtitle')}
          </Text>
        </View>

        <ProfileForm
          initial={profile}
          submitLabel={t('onboarding.continue')}
          onSubmit={async (patch) => {
            await save(patch);
            const pendingAgreementId = await consumePendingInviteAgreement();
            if (pendingAgreementId) {
              // Ο κωδικός πρόσκλησης αποδεικνύει ότι είναι ο παραλήπτης — τον
              // δηλώνει αντισυμβαλλόμενο ακόμα κι αν εγγράφηκε με άλλο email.
              await claimPendingInviteAgreement(pendingAgreementId);
              router.replace({ pathname: '/(app)/agreements/[id]', params: { id: pendingAgreementId } });
            } else {
              router.replace({ pathname: '/(app)/profile', params: { tour: '1' } });
            }
          }}
        />
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  flex: { flex: 1, backgroundColor: colors.background },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
  },
  header: {
    gap: spacing.sm,
  },
  title: {
    color: colors.text,
    fontSize: fontSize.xl,
    fontWeight: '700',
  },
  subtitle: {
    color: colors.textMuted,
    fontSize: fontSize.md,
  },
  });
}
